import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { WorkspaceGuide, WorkspaceViews } from '../components/WorkspaceExperience';
import styled from 'styled-components';
import { ShieldCheck } from 'lucide-react';
import type { ActionFeedbackState } from '../components/ActionFeedback';
import CovenantMonitoringPanel from './CovenantMonitoringPanel';
import PoolTrendCard from './PoolTrendCard';
import RollRateCard from './RollRateCard';
import UnderwritingCaseCard from './UnderwritingCaseCard';
import ExpectedVsActualCard from './ExpectedVsActualCard';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Slice 4 (F-S4-010) — the Monitoring tab is the covenant panel.
 *
 * It used to be six stacked blocks. The covenant panel was mounted above the sections
 * it superseded rather than replacing them, so the same six exceptions were rendered
 * four times by four different queries, and a pool-metric card grid restated the tape
 * freshness, approved base and official exposure that the persistent facility header
 * already carries above every tab.
 *
 * What went, and why none of it is a capability loss:
 *  - Exception work queue and contract operations: the panel does both.
 *  - Pool-metric cards: delinquency and net loss moved into the panel's surveillance
 *    section, each shown against the governed threshold that tests it or labelled as
 *    having none. One reading of each number instead of two.
 *  - Frozen authority grid: replaced by one provenance line on the covenant table
 *    naming the contract version, what fired the test and its fingerprint
 *    (FR-MON-001). The persistent header owns the rest.
 *  - Valuation and scenarios: moved to their own route (operator decision 2026-09-16),
 *    matching how every comparable platform files valuation.
 *
 * Cash and remittance intake stays here, collapsed, until Slice 5 absorbs it into the
 * Collections period workspace as the deliverables step (FRD section 12.7). It is the
 * one thing on this tab belonging to another job, and moving it early would strand it.
 */

const Shell = styled.div`display: flex; flex-direction: column; gap: .85rem; margin-top: .85rem;`;

const Boundary = styled.div`padding:.7rem .8rem;border:1px solid rgba(52,211,153,.28);border-radius:10px;background:rgba(6,78,59,.12);color:#a7f3d0;font-size:.68rem;line-height:1.45;`;

interface Props {
  facilityId: number;
  currency?: string | null;
  /** The active contract, so a covenant run can be named by its version number rather than its row id. */
  contractVersion?: { recordId: number | null; versionNumber: number | null };
  onFeedback: (feedback: ActionFeedbackState) => void;
}

const MAKER_ROLES = new Set(['PM', 'PORTFOLIO_MANAGER', 'RISK', 'RISK_MANAGER', 'OPERATIONS', 'OPERATIONS_ANALYST', 'MIDDLE_OFFICE_ADMIN', 'ADMIN', 'PLATFORM_ADMIN']);

const MonitoringWorkspace: React.FC<Props> = ({ facilityId, currency, contractVersion, onFeedback }) => {
  const [params, setParams] = useSearchParams();
  const lenses = [{key:'controls',label:'Covenants & exceptions'},{key:'trends',label:'Pool trends & migration'},{key:'expectations',label:'Underwriting & collections'}];
  const lens = lenses.some(l => l.key === params.get('monitorView')) ? params.get('monitorView')! : 'controls';
  const ccy = currency || 'USD';
  const { user } = useAuth();
  const canEditCase = MAKER_ROLES.has(String(user?.role || '').toUpperCase());
  return <Shell>
    <WorkspaceGuide><h2>Review risk, then act on the evidence</h2><p>Covenants and exceptions show the latest control results and contractual consequences. Explore deterioration over time or compare actual performance with underwriting in the views below. Each analysis names its own source date.</p></WorkspaceGuide>
    <WorkspaceViews label="Monitoring views" value={lens} items={lenses} onChange={key => { const next = new URLSearchParams(params); next.set('monitorView', key); setParams(next); }} />
    <div hidden={lens !== 'controls'}>
    <CovenantMonitoringPanel facilityId={facilityId} currency={ccy} contractVersion={contractVersion} onFeedback={onFeedback} />

    </div><div hidden={lens !== 'trends'}>
    {/* WS2: the pool's history, one point per released tape, beneath the covenants it feeds. */}
    <PoolTrendCard facilityId={facilityId} currency={ccy} onFeedback={onFeedback} />

    {/* F-MK-075: where the loans went between the two latest released tapes, beside the pool's history. */}
    <RollRateCard facilityId={facilityId} currency={ccy} onFeedback={onFeedback} />

    </div><div hidden={lens !== 'expectations'}>
    {/* WS5 D-8: what the deal was underwritten to, beside the latest actual; shipped in the data feed. */}
    <UnderwritingCaseCard facilityId={facilityId} canEdit={canEditCase} onFeedback={onFeedback} />

    {/* WS7: what the tape said the period would collect, against what it did. */}
    <ExpectedVsActualCard facilityId={facilityId} currency={ccy} onFeedback={onFeedback} />

    </div>
    <Boundary><ShieldCheck size={14} style={{verticalAlign:'middle',marginRight:6}}/>Monitoring records evidence and applies contractual consequences. It never changes the approved borrowing base, cash, a position or a mark; the official position record remains the economic authority.</Boundary>
  </Shell>;
};

export default MonitoringWorkspace;
